import i18n from "@/i18n";
import expressLogo from "@/assets/linkedpay/express.png";
import {
  applyCountryCurrencyCode,
  checkPaymentPhone,
  findMnoInfoByBankCode,
  formatDecimalByPlaces,
  getPaymentPhoneMaxLength,
  zeroFillPaymentPhone,
} from "@/utils/countryAdapter";

export default {
  name: "LinkedPayVALogicMixin",
  props: {
    pageData: Object,
  },
  watch: {
    pageData(val) {
      this.repaymentData = val;
      this.updateCode();
      if (val?.paymentData?.expireTime) {
        this.repaymentData.remainTime = parseInt(val.paymentData.expireTime);
        // 重新启动倒计时，使用新的 remainTime
        clearTimeout(this.setTimeoutID);
        this.futureDay();
      }
    },
  },
  data() {
    return {
      setTimeoutID: undefined,
      silentPollTimer: null,
      repaymentData: {},
      vaAccount: "",
      bankCode: "",
      hr: undefined,
      min: undefined,
      sec: undefined,
      paymentAccount: "",
      userSubmitAccountNumber: "",
      paymentAccountSubmitting: false,
      expressLogo,
    };
  },
  computed: {
    countryInfo() {
      return this.repaymentData?.configData?.countryInfoVO;
    },
    currentBankInfo() {
      const bankCode = this.repaymentData?.paymentData?.bankCode;
      return findMnoInfoByBankCode(this.repaymentData, bankCode) || {};
    },
    formattedPaymentAmount() {
      const amount = this.repaymentData?.paymentData?.amount;
      if (amount == null) {
        return amount;
      }
      return formatDecimalByPlaces(
        String(amount),
        this.currentBankInfo?.decimalPlaces
      );
    },
    alreadyInitiated() {
      // >= THIRD_RESPONSE_SUCCESS：VA 已生成，sys/initialize 已调用过
      return this.checkoutStatusToNumber(this.pageData?.paymentData?.checkoutStatus) >= 30;
    },
    // 是否已提交号码（持久化在 pageData.paymentData.checkoutStatus 中，刷新不丢）
    // 判定：checkoutStatus >= 30（THIRD_RESPONSE_SUCCESS 及之后的状态）
    // status 可能是数字（30/40/...）或字符串枚举名（"THIRD_RESPONSE_SUCCESS"/...）
    isAccountSubmitted() {
      const status = this.pageData?.paymentData?.checkoutStatus;
      const statusVal = status?.value ?? status;
      const numericVal = this.checkoutStatusToNumber(statusVal);
      return Number(numericVal) >= 30;
    },
    phoneMaxLength() {
      return getPaymentPhoneMaxLength(this.countryInfo);
    },
    shouldShowNumberInput() {
      return this.repaymentData?.extraParams?.includes("customerAccountNumber");
    },
    isAO() {
      return this.countryInfo?.enName === "AO";
    },
    // AO 国家：未提交号码时为"收银台前置页"（用 "Valor do pagamento"）
    //        已提交后为"收银台页"（用 "Valor"）
    vaAmountLabelKey() {
      if (!this.isAO) {
        return "Amount";
      }
      // 直接从 pageData 读 status，避免 computed 间的循环引用
      const status = this.pageData?.paymentData?.checkoutStatus;
      const statusVal = status?.value ?? status;
      if (this.checkoutStatusToNumber(statusVal) >= 30) {
        return "text_va_amount";
      }
      return "text_va_amount_pre";
    },
  },
  methods: {
    // 把 checkoutStatus（数字 / 字符串枚举名）统一映射为数字
    checkoutStatusToNumber(s) {
      if (typeof s === "number") return s;
      if (typeof s !== "string") return -1;
      const map = {
        INIT: 0,
        OPENED: 10,
        PREPARE_INFO_SUBMITTED: 20,
        THIRD_RESPONSE_SUCCESS: 30,
        ACCOUNT_NUMBER_SUBMITTED: 40,
        SMS_SUBMITTED: 50,
        TX_ID_SUBMITTED: 60,
        FAIL: 99,
        SUCCESS: 100,
      };
      return map[s] ?? -1;
    },
    formatMoney(val) {
      if (!val) return val;
      return parseFloat(val).toFixed(2);
    },
    updateCode() {
      applyCountryCurrencyCode(this.repaymentData);
    },
    onCopy(str) {
      if (!str || str === "--") {
        this.$toast("No value to copy");
        return;
      }
      let oInput = document.createElement("input");
      oInput.value = str;
      document.body.appendChild(oInput);
      oInput.select();
      document.execCommand("Copy");
      this.$toast(this.$t("text_successful_replication"));
      oInput.remove();
    },
    futureDay() {
      clearTimeout(this.setTimeoutID);
      const msec = this.repaymentData?.remainTime || 0;
      this.repaymentData.remainTime -= 1000;
      let day = parseInt(msec / 1000 / 60 / 60 / 24);
      let hr = parseInt((msec / 1000 / 60 / 60) % 24) + day * 24;
      let min = parseInt((msec / 1000 / 60) % 60);
      let sec = parseInt((msec / 1000) % 60);
      hr = hr > 99 ? 99 : hr;
      if (isNaN(hr) || isNaN(min) || isNaN(sec)) { hr = 0; min = 0; sec = 0; }
      hr = hr < 0 ? 0 : hr;
      min = min < 0 ? 0 : min;
      sec = sec < 0 ? 0 : sec;
      this.hr = hr > 9 ? hr : "0" + hr;
      this.min = min > 9 ? min : "0" + min;
      this.sec = sec > 9 ? sec : "0" + sec;
      this.setTimeoutID = setTimeout(() => { this.futureDay(); }, 1000);
      if (hr <= 0 && min <= 0 && sec <= 0) {
        clearTimeout(this.setTimeoutID);
        this.$emit("expired");
      }
    },
    back() {
      window.opener = null;
      window.open("", "_self");
      window.close();
    },
    async fetchVAInfo() {
      // 如果需要收号码，先让用户提交；不在 created 阶段调 sys/initialize
      if (this.repaymentData?.extraParams?.includes("customerAccountNumber")) {
        // 等用户提交号码后由 paymentAccountSubmit 触发 silentPoll
        return;
      }
      // 不需要收号码：直接 sys/initialize + silentPoll
      if (!this.alreadyInitiated) {
        await this.$http.v1SdkSysInitialize({
          platformReference: this.$route.params.platformReference,
        }).catch(() => {});
      }
      await this.silentPoll();
      // 通知父组件开始全局 polling
      this.$emit("paymentInitiated");
    },
    // 默默轮询 VA 信息
    async silentPoll() {
      try {
        const displayRes = await this.$http.channelDisplay(this.$route.params.platformReference).catch(() => null);
        if (displayRes?.code == "200" && displayRes.data) {
          if (displayRes.data.vaAccount) this.vaAccount = displayRes.data.vaAccount;
          if (displayRes.data.bankCode) this.bankCode = displayRes.data.bankCode;
        }
      } catch (e) {
        // 默默忽略
      }
    },
    // 校验：号码格式是否合法
    checkPhone(value) {
      if (!this.repaymentData.extraParams || !this.repaymentData.extraParams.includes("customerAccountNumber")) {
        return true;
      }
      return checkPaymentPhone(value, this.countryInfo);
    },
    // 校验：号码正则（仅在 BJ 国家使用）
    checkPhoneRegex(value) {
      const prefixList = [
        "0142", "0146", "0150", "0151", "0152", "0153", "0154", "0156",
        "0157", "0159", "0161", "0162", "0166", "0167", "0169", "0190",
        "0191", "0196", "0197", "0145", "0155", "0158", "0160", "0163",
        "0164", "0165", "0168", "0194", "0195", "0198", "0199",
      ];
      const prefixListWithoutZero = prefixList.map((prefix) => prefix.substring(1));
      if (!/^\d+$/.test(value)) {
        return false;
      }
      if (value.startsWith("0") && value.length === 10) {
        return prefixList.includes(value.substring(0, 4));
      } else if (!value.startsWith("0") && value.length === 9) {
        return prefixListWithoutZero.includes(value.substring(0, 3));
      }
      return false;
    },
    // 校验：回显时校验账号格式
    validateAccountNumberFormat(value) {
      if (!value) return false;
      return checkPaymentPhone(value, this.countryInfo);
    },
    // 补 0：缺 0 时加前导 0
    zeroFill(value) {
      return zeroFillPaymentPhone(value, this.countryInfo);
    },
    // 提交号码：精简版（无 operatorList / 无 verify 弹窗）
    async paymentAccountSubmit() {
      if (!this.paymentAccount) {
        this.$toast(this.$t("text_please_enter_payment_account_number"));
        return;
      }
      let userSubmitAccountNumber = this.paymentAccount;
      if (!this.checkPhone(userSubmitAccountNumber)) {
        this.$toast(this.$t("text_mobile_phone_number_format_incorrect"));
        return;
      }
      if (
        this.countryInfo?.enName === "BJ" &&
        !this.checkPhoneRegex(userSubmitAccountNumber)
      ) {
        this.$toast(this.$t("text_mobile_phone_number_format_incorrect"));
        return;
      }
      userSubmitAccountNumber = this.zeroFill(userSubmitAccountNumber).trim();
      this.userSubmitAccountNumber = userSubmitAccountNumber;

      this.paymentAccountSubmitting = true;
      try {
        const res = await this.$http.v1SdkSysInitialize({
          platformReference: this.$route.params.platformReference,
          paymentAccountNumber: this.userSubmitAccountNumber,
        });
        if (res && res.code == "200") {
          // 触发一次轮询，让 VA/Bank Code 立即可见
          await this.silentPoll();
          // 通知父组件重新拉 pageData（pageData.paymentData.checkoutStatus 变 40）
          // 子组件 isAccountSubmitted computed 由 pageData 驱动，刷新后三排自然出现
          this.$emit("refreshData");
        } else {
          this.$toast((res && res.message) || "Submit failed");
        }
      } catch (e) {
        this.$toast("Submit failed");
      } finally {
        this.paymentAccountSubmitting = false;
      }
    },
  },
  created() {
    this.repaymentData = this.pageData;
    this.updateCode();
    // 先赋值 repaymentData，再读 countryInfo 做 locale 切换
    if (this.countryInfo?.enName === "AO") {
      i18n.locale = "pt";
      localStorage.setItem("lang", "pt");
    }
    if (this.repaymentData?.paymentData?.expireTime) {
      this.repaymentData.remainTime = parseInt(this.repaymentData.paymentData.expireTime);
    }
    this.futureDay();
    this.fetchVAInfo();
    // 启动默默轮询：定期刷新 VA/Bank Code（不显示 loading）
    this.silentPollTimer = setInterval(() => this.silentPoll(), 5000);
  },
  beforeDestroy() {
    clearTimeout(this.setTimeoutID);
    if (this.silentPollTimer) {
      clearInterval(this.silentPollTimer);
      this.silentPollTimer = null;
    }
  },
};
